import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Import all section components
import SiteInventorySection from './components/SiteInventorySection';
import LocationSection from './components/LocationSection';
import UtilityHookupSection from './components/UtilityHookupSection';
import SiteFeaturesSection from './components/SiteFeaturesSection';
import PricingSection from './components/PricingSection';
import RevenueMultiplierSection from './components/RevenueMultiplierSection';
import OperationalSection from './components/OperationalSection';
import CalculationPanel from './components/CalculationPanel';
import ProgressIndicator from './components/ProgressIndicator';
import SEOContentSection from './components/SEOContentSection';

const RVParkCalculator = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [hasViewedCalculation, setHasViewedCalculation] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    siteInventory: {
      shortTermSites: 0,
      longTermSites: 0
    },
    location: {
      address: ''
    },
    utilities: {
      power: false,
      water: false,
      sewer: false
    },
    siteFeatures: [],
    pricing: {
      nightly: 45,
      weekly: 280,
      monthly: 950
    },
    revenueMultipliers: [],
    operational: {
      occupancyRate: 65,
      daysOpenPerMonth: 30,
      costModel: 'standard'
    }
  });

  const sectionTitles = [
    'Site Inventory',
    'Location Details',
    'Utility Hookups',
    'Site Features',
    'Pricing Strategy',
    'Revenue Multipliers',
    'Operational Parameters'
  ];

  // Update form data function with localStorage persistence
  const updateFormData = (section, data) => {
    const newFormData = {
      ...formData,
      [section]: data
    };
    setFormData(newFormData);
    setHasViewedCalculation(true);
    
    // Save to localStorage for persistent validation
    localStorage.setItem('rvParkCalculatorData', JSON.stringify(newFormData));
    localStorage.setItem('rvParkCalculatorCompleted', JSON.stringify(validateFormData(newFormData)));
  };

  // Enhanced form completion validation function
  const validateFormData = (data = formData) => {
    // Section 0: Site Inventory - Required
    const hasSites = data?.siteInventory?.shortTermSites > 0 || data?.siteInventory?.longTermSites > 0;
    
    // Section 1: Location - Required  
    const hasLocation = data?.location?.address?.trim();
    
    // Section 2: Utilities - At least one required
    const hasUtilities = data?.utilities?.power || data?.utilities?.water || data?.utilities?.sewer;
    
    // Section 4: Pricing - Valid rates required
    const hasPricing = data?.pricing?.nightly > 0;
    
    // Section 6: Operational - Required
    const hasOperational = data?.operational?.daysOpenPerMonth > 0 && data?.operational?.occupancyRate > 0;
    
    return hasSites && hasLocation && hasUtilities && hasPricing && hasOperational;
  };

  // Form completion validation function
  const validateAllSections = () => {
    const validationErrors = {};
    let isComplete = true;

    // Section 0: Site Inventory - Required
    if (!formData?.siteInventory?.shortTermSites && !formData?.siteInventory?.longTermSites) {
      validationErrors.siteInventory = 'At least one type of site is required';
      isComplete = false;
    }

    // Section 1: Location - Required
    if (!formData?.location?.address?.trim()) {
      validationErrors.location = 'Location is required';
      isComplete = false;
    }

    // Section 2: Utilities - Optional but should have at least one utility for meaningful calculation
    const hasUtilities = formData?.utilities?.power || formData?.utilities?.water || formData?.utilities?.sewer;
    if (!hasUtilities) {
      validationErrors.utilities = 'At least one utility should be selected for accurate projections';
      isComplete = false;
    }

    // Section 4: Pricing - Already has defaults, validate they're reasonable
    if (!formData?.pricing?.nightly || formData?.pricing?.nightly < 1) {
      validationErrors.pricing = 'Valid nightly pricing is required';
      isComplete = false;
    }

    // Section 6: Operational - Required
    if (!formData?.operational?.daysOpenPerMonth || formData?.operational?.daysOpenPerMonth < 1) {
      validationErrors.operational = 'Days open per month is required';
      isComplete = false;
    }
    if (!formData?.operational?.occupancyRate || formData?.operational?.occupancyRate < 1) {
      validationErrors.operational = 'Valid occupancy rate is required';
      isComplete = false;
    }

    return { isComplete, validationErrors };
  };

  // Check if form is complete for projections access
  const isFormComplete = () => {
    return validateFormData();
  };

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('rvParkCalculatorData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error loading saved calculator data:', error);
      }
    }
  }, []);

  // Navigation functions
  const handleNext = () => {
    if (validateCurrentSection()) {
      if (currentSection < sectionTitles?.length - 1) {
        setCurrentSection(currentSection + 1);
      }
    }
  };

  // Add validateCurrentSection function
  const validateCurrentSection = () => {
    const validationErrors = {};
    let isValid = true;

    switch (currentSection) {
      case 0: // Site Inventory
        if (!formData?.siteInventory?.shortTermSites && !formData?.siteInventory?.longTermSites) {
          validationErrors.siteInventory = 'At least one type of site is required';
          isValid = false;
        }
        break;
      case 1: // Location
        if (!formData?.location?.address?.trim()) {
          validationErrors.location = 'Location is required';
          isValid = false;
        }
        break;
      case 4: // Pricing
        if (!formData?.pricing?.nightly || formData?.pricing?.nightly < 1) {
          validationErrors.pricing = 'Valid nightly pricing is required';
          isValid = false;
        }
        break;
      case 6: // Operational
        if (!formData?.operational?.daysOpenPerMonth || formData?.operational?.daysOpenPerMonth < 1) {
          validationErrors.operational = 'Days open per month is required';
          isValid = false;
        }
        if (!formData?.operational?.occupancyRate || formData?.operational?.occupancyRate < 1) {
          validationErrors.operational = 'Valid occupancy rate is required';
          isValid = false;
        }
        break;
      default:
        // Other sections are optional or have no strict validation
        break;
    }

    setErrors(validationErrors);
    return isValid;
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  // Enhanced handleViewProjections with localStorage update
  const handleViewProjections = () => {
    const completedFormData = { ...formData };
    
    // Save completion state to localStorage
    localStorage.setItem('rvParkCalculatorData', JSON.stringify(completedFormData));
    localStorage.setItem('rvParkCalculatorCompleted', JSON.stringify(true));
    localStorage.setItem('rvParkCalculatorTimestamp', new Date()?.toISOString());
    
    // Navigate with state data
    navigate('/financial-projections', { state: { formData: completedFormData } });
  };

  // Calculate totals for display
  const calculations = {
    totalSites: (formData?.siteInventory?.shortTermSites || 0) + (formData?.siteInventory?.longTermSites || 0),
    hasUtilities: formData?.utilities?.power || formData?.utilities?.water || formData?.utilities?.sewer,
    totalFeatures: formData?.siteFeatures?.length,
    totalMultipliers: formData?.revenueMultipliers?.length,
    isFormComplete: isFormComplete() // Add form completion status
  };

  // Render current section
  const renderCurrentSection = () => {
    switch (currentSection) {
      case 0:
        return (
          <SiteInventorySection
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 1:
        return (
          <LocationSection
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <UtilityHookupSection
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 3:
        return (
          <SiteFeaturesSection
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 4:
        return (
          <PricingSection
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 5:
        return (
          <RevenueMultiplierSection
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 6:
        return (
          <OperationalSection
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              RV Park Income Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Calculate your potential RV park income with our comprehensive financial analysis tool. 
              Get personalized projections based on your specific property and business model.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form Area */}
            <div className="lg:col-span-2 space-y-6">
              <ProgressIndicator
                currentSection={currentSection}
                totalSections={sectionTitles?.length}
                sectionTitles={sectionTitles}
              />

              {/* Current Section */}
              {renderCurrentSection()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-6">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentSection === 0}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Shield" size={16} />
                  <span>Your data is secure and private</span>
                </div>

                <Button
                  onClick={handleNext}
                  disabled={currentSection === sectionTitles?.length - 1}
                  iconName={currentSection === sectionTitles?.length - 1 ? "Check" : "ChevronRight"}
                  iconPosition="right"
                >
                  {currentSection === sectionTitles?.length - 1 ? "Complete" : "Next"}
                </Button>
              </div>
            </div>

            {/* Calculation Panel */}
            <div className="lg:col-span-1">
              <CalculationPanel
                formData={formData}
                calculations={calculations}
                onViewProjections={handleViewProjections}
              />
            </div>
          </div>

          {/* Trust Signals */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                  <Icon name="Shield" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="font-medium text-foreground">Secure & Private</h3>
                <p className="text-sm text-muted-foreground">Your data is encrypted and never shared</p>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                  <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="font-medium text-foreground">Industry Accurate</h3>
                <p className="text-sm text-muted-foreground">Based on real RV park performance data</p>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                  <Icon name="Users" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="font-medium text-foreground">Trusted by 1000+</h3>
                <p className="text-sm text-muted-foreground">Landowners and investors nationwide</p>
              </div>
            </div>
          </div>

          {/* SEO Content Section */}
          <SEOContentSection />
        </div>
      </div>
    </div>
  );
};

export default RVParkCalculator;